import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSocket } from "../../context/SocketContext";
import api from "../../utils/api";
import toast from "react-hot-toast";
import {
  Send, MessageCircle, ArrowLeft, Search, UserPlus, Check,
} from "lucide-react";
import "./ChatPage.css";

export default function ChatPage() {
  const { conversationId } = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();
  const socketCtx = useSocket();
  const { joinConversation, leaveConversation, sendMessage, onNewMessage, offNewMessage } = socketCtx;

  const [conversations, setConversations] = useState([]);
  const [activeConvo,   setActiveConvo]   = useState(null);
  const [messages,      setMessages]      = useState([]);
  const [text,          setText]          = useState("");
  const [search,        setSearch]        = useState("");
  const [results,       setResults]       = useState([]);
  const [confirmedIds,  setConfirmedIds]  = useState(new Set());

  const bottomRef = useRef(null);
  const inputRef  = useRef(null);
  const autoStarted = useRef(false); // prevent double-fire

  // ── Load conversations ────────────────────────────────────
  useEffect(() => {
    api.get("/chat/conversations")
      .then(({ data }) => setConversations(data.conversations))
      .catch(() => toast.error("Could not load chats."));
  }, []);

  // ── Search users (debounced) ──────────────────────────────
  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/chat/search-users?q=${search}`);
        setResults(data.users);
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── Socket: receive messages ──────────────────────────────
  useEffect(() => {
    if (!socketCtx?.socket) return;
    const handler = (msg) => {
      if (activeConvo && msg.conversation === activeConvo._id) {
        setMessages((prev) => {
          const optIdx = prev.findIndex(
            (m) =>
              m._id?.startsWith("opt_") &&
              m.text === msg.text &&
              m.sender?._id?.toString() === msg.sender?._id?.toString()
          );
          if (optIdx !== -1) {
            setConfirmedIds((s) => new Set(s).add(msg._id));
            const next = [...prev];
            next[optIdx] = msg;
            return next;
          }
          return [...prev, msg];
        });
      } else {
        setConversations((prev) =>
          prev.map((c) =>
            c._id === msg.conversation
              ? {
                  ...c,
                  lastMessage: { text: msg.text, createdAt: msg.createdAt },
                  unreadCount: {
                    ...c.unreadCount,
                    [user._id]: (c.unreadCount?.[user._id] || 0) + 1,
                  },
                }
              : c
          )
        );
      }
    };
    onNewMessage(handler);
    return () => offNewMessage(handler);
  }, [activeConvo, socketCtx?.socket]);

  // ── Scroll ────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Mobile keyboard-safe viewport height ──────────────────
  useEffect(() => {
    const setVh = () =>
      document.documentElement.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
    setVh();
    window.addEventListener("resize", setVh);
    return () => window.removeEventListener("resize", setVh);
  }, []);

  // ── Auto-open chat with seller (from "Chat with Seller" button) ──
  useEffect(() => {
    if (!user || autoStarted.current) return;
    const { sellerId, productTitle } = location.state || {};
    if (!sellerId) return;

    autoStarted.current = true; // prevent double-fire on re-renders

    const autoMsg = productTitle
      ? `Hey! I'm interested in your listing "${productTitle}". Is it still available?`
      : "Hey! I'm interested in your product. Is it still available?";

    startChatWith(sellerId, autoMsg);

    // Clear navigation state so a page refresh doesn't retrigger
    window.history.replaceState({}, document.title);
  }, [user]);

  // ── Helpers ───────────────────────────────────────────────
  const idStr    = (v) => v?._id?.toString() ?? v?.toString() ?? "";
  const getOther = (convo) =>
    convo?.participants?.find((p) => idStr(p) !== idStr(user));
  const getUnread = (c) =>
    c.unreadCount?.[user._id] || c.unreadCount?.get?.(user._id) || 0;
  const formatTime = (d) =>
    new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  // ── Open conversation ─────────────────────────────────────
  const openConversation = async (convo) => {
    if (activeConvo?._id === convo._id) return;
    if (activeConvo) leaveConversation(activeConvo._id);

    setActiveConvo(convo);
    setMessages([]);
    setConfirmedIds(new Set());

    // Clear unread in UI immediately
    setConversations((prev) =>
      prev.map((c) =>
        c._id === convo._id
          ? { ...c, unreadCount: { ...c.unreadCount, [user._id]: 0 } }
          : c
      )
    );

    joinConversation(convo._id);
    navigate("/app/chat/" + convo._id);

    try {
      const { data } = await api.get("/chat/messages/" + convo._id);
      setConfirmedIds(new Set(data.messages.map((m) => m._id)));
      setMessages(data.messages);
    } catch {
      toast.error("Failed to load messages");
    }
  };

  // ── Start chat from search (or from "Chat with Seller") ───
  const startChatWith = async (userId, autoMessage = null) => {
    try {
      const { data } = await api.post("/chat/conversation", { recipientId: userId });
      const convo = data.conversation;

      setConversations((prev) =>
        prev.find((c) => c._id === convo._id) ? prev : [convo, ...prev]
      );

      openConversation(convo);
      setSearch("");
      setResults([]);

      // Send the auto-message after a short delay so the socket room is joined
      if (autoMessage) {
        setTimeout(() => {
          // Optimistic UI update
          setMessages((prev) => [
            ...prev,
            {
              _id: "opt_" + Date.now(),
              conversation: convo._id,
              sender: user,
              text: autoMessage,
              createdAt: new Date().toISOString(),
            },
          ]);
          // Send via socket (primary) — server also broadcasts back confirmed message
          sendMessage({ conversationId: convo._id, text: autoMessage });
        }, 600);
      }
    } catch {
      toast.error("Could not start chat");
    }
  };

  // ── Send message ──────────────────────────────────────────
  const handleSend = (e) => {
    e.preventDefault();
    if (!text.trim() || !activeConvo) return;
    setMessages((prev) => [
      ...prev,
      {
        _id: "opt_" + Date.now(),
        conversation: activeConvo._id,
        sender: user,
        text,
        createdAt: new Date().toISOString(),
      },
    ]);
    sendMessage({ conversationId: activeConvo._id, text });
    setText("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // ── Tick ──────────────────────────────────────────────────
  const MessageTick = ({ message }) => {
    if (idStr(message.sender) !== idStr(user)) return null;
    const confirmed = confirmedIds.has(message._id) || !message._id?.startsWith("opt_");
    return (
      <span className={"chat-tick " + (confirmed ? "chat-tick-sent" : "chat-tick-pending")}>
        <Check size={11} strokeWidth={2.5} />
      </span>
    );
  };

  if (!user) {
    return (
      <div className="chat-page" style={{ alignItems: "center", justifyContent: "center", display: "flex" }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="chat-page">

      {/* ── SIDEBAR ──────────────────────────────────────── */}
      <div className={"chat-sidebar" + (activeConvo ? " hide-mobile" : "")}>
        <div className="chat-sidebar-header">
          <h3>Chats</h3>
          <UserPlus size={18} />
        </div>

        <div className="chat-search-row">
          <Search size={14} className="chat-search-ico" />
          <input
            className="chat-search-inp"
            placeholder="Search by name or @username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {results.length > 0 && (
          <div className="new-chat-panel">
            <div className="new-chat-results">
              {results.map((u) => (
                <button key={u._id} className="new-chat-result-item" onClick={() => startChatWith(u._id)}>
                  <div className="new-chat-result-avatar">{u.name?.[0]?.toUpperCase()}</div>
                  <div className="new-chat-result-info">
                    <span className="new-chat-result-name">{u.name}</span>
                    <span className="new-chat-result-username">@{u.username}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="chat-list">
          {conversations.length === 0 ? (
            <div className="chat-empty-state">
              <p>No chats yet</p>
              <span>Search users to start chatting</span>
            </div>
          ) : (
            conversations.map((c) => {
              const other    = getOther(c);
              const unread   = getUnread(c);
              const isActive = activeConvo?._id === c._id;
              const hasUnread = unread > 0 && !isActive;

              return (
                <button
                  key={c._id}
                  className={"chat-list-item" + (isActive ? " active" : "") + (hasUnread ? " unread" : "")}
                  onClick={() => openConversation(c)}
                >
                  <div className="chat-list-avatar-wrap">
                    <div className="chat-list-avatar-circle">
                      {other?.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    {hasUnread && <span className="chat-avatar-dot" />}
                  </div>

                  <div className="chat-list-info">
                    <div className="chat-list-top">
                      <span className={"chat-list-name" + (hasUnread ? " bold" : "")}>
                        {other?.name}
                      </span>
                      {hasUnread && (
                        <span className="chat-unread-badge">{unread}</span>
                      )}
                    </div>
                    <span className={"chat-list-preview" + (hasUnread ? " bold" : "")}>
                      {c.lastMessage?.text || "Start chatting..."}
                    </span>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ── MAIN CHAT ─────────────────────────────────────── */}
      <div className="chat-main">
        {!activeConvo ? (
          <div className="chat-welcome">
            <div className="chat-welcome-icon"><MessageCircle size={36} /></div>
            <h4>Your Messages</h4>
            <p>Select a chat or search a user to start</p>
          </div>
        ) : (
          <>
            <div className="chat-header">
              <button
                className="chat-back"
                onClick={() => { setActiveConvo(null); navigate("/app/chat"); }}
              >
                <ArrowLeft size={18} />
              </button>

              <div
                className="chat-header-info chat-header-clickable"
                onClick={() => navigate("/app/profile/" + getOther(activeConvo)?._id?.toString())}
                role="button"
                tabIndex={0}
                title="View profile"
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  navigate("/app/profile/" + getOther(activeConvo)?._id?.toString())
                }
              >
                <div className="chat-header-avatar">
                  {getOther(activeConvo)?.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="chat-header-name">{getOther(activeConvo)?.name}</div>
                  <div className="chat-header-username">
                    @{getOther(activeConvo)?.username} · tap to view profile
                  </div>
                </div>
              </div>
            </div>

            <div className="chat-auto-delete-notice">
              ⏰ Messages disappear after 7 days
            </div>

            <div className="chat-messages">
              {messages.map((m) => {
                const isMine = idStr(m.sender) === idStr(user);
                return (
                  <div key={m._id} className={"chat-row " + (isMine ? "chat-row-me" : "chat-row-them")}>
                    <div className={"chat-bubble-col " + (isMine ? "col-me" : "col-them")}>
                      <div className={"chat-bubble " + (isMine ? "bubble-me" : "bubble-them")}>
                        {m.text}
                      </div>
                      <div className="chat-meta-row">
                        <span className="chat-time">{formatTime(m.createdAt)}</span>
                        <MessageTick message={m} />
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            <form className="chat-input-bar" onSubmit={handleSend}>
              <input
                ref={inputRef}
                className="chat-inp"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type a message..."
                autoComplete="off"
              />
              <button className="chat-send" type="submit" disabled={!text.trim()}>
                <Send size={16} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}